import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Column, useSortBy, useTable } from 'react-table';
import SelfMonitorApi from '../../../api/SelfMonitorApi';
import SortClass from '../../../component/SortClass';
import { RepositorySpace, HypertablesResult } from '../../../types/SelfMonitor';

interface DatabaseTabProps {
    refreshKey: number;
}

// pg_size_pretty() units, used to sort the size columns numerically rather
// than as strings (e.g. "1.2 MB" must sort above "900 kB").
const PRETTY_SIZE_UNITS: Record<string, number> = {
    bytes: 1,
    kb: 1024,
    mb: 1024 ** 2,
    gb: 1024 ** 3,
    tb: 1024 ** 4,
    pb: 1024 ** 5,
};

const splitPrettySize = (value: string) => /^([\d.]+)\s*(\w+)$/.exec((value || '').trim());

function parsePrettySize(value: string): number {
    const match = splitPrettySize(value);
    if (!match) {
        return 0;
    }
    const [, amount, unit] = match;
    return parseFloat(amount) * (PRETTY_SIZE_UNITS[unit.toLowerCase()] ?? 1);
}

const SpaceTile: React.FC<{ label: string; value?: string }> = ({ label, value }) => {
    const match = value ? splitPrettySize(value) : null;
    return (
        <div className="col">
            <div className="tile-label">{label}</div>
            <div className="tile-value">
                {match ? match[1] : (value ?? '--')}
                {match && <span className="tile-unit">{match[2]}</span>}
            </div>
        </div>
    );
};

const sortByPrettySize = (rowA: any, rowB: any, columnId: string) =>
    parsePrettySize(rowA.values[columnId]) - parsePrettySize(rowB.values[columnId]);

// Repository sizing (phase 2): space_used.sql and list_hypertables.sql, run
// directly over the existing repository connection. Space usage is cheap
// and follows the screen's normal refresh; the hypertable breakdown calls
// pg_total_relation_size() over every table and is expensive, so it is
// fetched once on mount and otherwise only on an explicit Refresh click -
// see SelfMonitorServlet's few-minutes server-side cache of the same query.
const DatabaseTab: React.FC<DatabaseTabProps> = ({ refreshKey }) => {
    const [space, setSpace] = useState<RepositorySpace | null>(null);
    const [hypertables, setHypertables] = useState<HypertablesResult | null>(null);
    const [loadingHypertables, setLoadingHypertables] = useState(true);

    useEffect(() => {
        SelfMonitorApi.getRepositorySpace().then(setSpace);
    }, [refreshKey]);

    const fetchHypertables = useCallback(async (forceRefresh?: boolean) => {
        setLoadingHypertables(true);
        try {
            setHypertables(await SelfMonitorApi.getHypertables(forceRefresh));
        } finally {
            setLoadingHypertables(false);
        }
    }, []);

    useEffect(() => {
        fetchHypertables();
    }, [fetchHypertables]);

    const columns: Column[] = useMemo(() => [
        {
            Header: 'Name',
            accessor: 'displayname',
            sortDescFirst: false,
        },
        {
            Header: 'Chunks',
            accessor: 'numchunks',
            headerClassName: 'text-end',
            className: 'text-end',
            sortDescFirst: true,
            sortType: 'basic',
            Cell: ({ value }: any) => value || '--',
        },
        {
            Header: 'Rows',
            accessor: 'rowcount',
            headerClassName: 'text-end',
            className: 'text-end',
            sortDescFirst: true,
            sortType: 'basic',
            Cell: ({ value }: any) => value.toLocaleString(),
        },
        {
            Header: 'Table Size',
            accessor: 'tablesize',
            headerClassName: 'text-end',
            className: 'text-end',
            sortDescFirst: true,
            sortType: sortByPrettySize,
        },
        {
            Header: 'Indexes Size',
            accessor: 'indexessize',
            headerClassName: 'text-end',
            className: 'text-end',
            sortDescFirst: true,
            sortType: sortByPrettySize,
        },
        {
            Header: 'Total Size',
            accessor: 'totalsize',
            headerClassName: 'text-end',
            className: 'text-end',
            sortDescFirst: true,
            sortType: sortByPrettySize,
        },
    ], []);

    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
        {
            columns,
            data: hypertables?.hypertables ?? [],
            // Matches list_hypertables.sql's own default order (largest first)
            // until the user picks a column to sort by.
            initialState: { sortBy: [{ id: 'totalsize', desc: true }] },
            autoResetSortBy: false,
        },
        useSortBy
    );

    return (
        <div className="self-monitor-database">
            <div className="card">
                <div className="card-header">
                    <i className="fal fa-database fa-fw" aria-hidden="true" />
                    Repository Space Used
                </div>
                <div className="card-body">
                    <div className="self-monitor-database__space row row-cols-sm-3 row-cols-1">
                        <SpaceTile label="TABLE DATA" value={space?.tablesize} />
                        <SpaceTile label="INDEXES" value={space?.indexsize} />
                        <SpaceTile label="TOTAL" value={space?.totalsize} />
                    </div>
                </div>
            </div>

            <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <span>
                        <i className="fal fa-table fa-fw mr-10" aria-hidden="true" />
                        Table Sizes
                    </span>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => fetchHypertables(true)}
                        disabled={loadingHypertables}
                    >
                        <i className="fal fa-arrows-rotate fa-fw" aria-hidden="true" />
                        {loadingHypertables ? 'Refreshing...' : 'Refresh'}
                    </button>
                </div>
                <div className="card-body">
                    {hypertables && !hypertables.available && (
                        <p className="text-muted mb-0">
                            TimescaleDB was not detected on this repository - table size breakdown is unavailable.
                        </p>
                    )}

                    {hypertables && hypertables.available && hypertables.hypertables.length === 0 && !loadingHypertables && (
                        <p className="text-muted mb-0">No hypertables found.</p>
                    )}

                    {hypertables && hypertables.available && hypertables.hypertables.length > 0 && (
                        <div className="table-responsive">
                            <table {...getTableProps()} className="table table-sm table-hover mb-0">
                                <thead>
                                    {headerGroups.map(headerGroup => (
                                        <tr {...headerGroup.getHeaderGroupProps()}>
                                            {headerGroup.headers.map(column => (
                                                <th {...column.getHeaderProps(column.getSortByToggleProps())}
                                                    className={(column as any).headerClassName}>
                                                    {column.render('Header')}
                                                    <SortClass column={column} />
                                                </th>
                                            ))}
                                        </tr>
                                    ))}
                                </thead>
                                <tbody {...getTableBodyProps()}>
                                    {rows.map(row => {
                                        prepareRow(row);
                                        return (
                                            <tr {...row.getRowProps()}>
                                                {row.cells.map(cell => (
                                                    <td {...cell.getCellProps()} className={(cell.column as any).className}>
                                                        {cell.render('Cell')}
                                                    </td>
                                                ))}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DatabaseTab;
